import { useState } from "react";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ModalPortal from "./ModalPortal";
import userService from "../services/userService";

// A small modal that invites a user by email. The person receives an invite and
// sets up their own account, so this form only needs the email address.
const InviteUserModalV2 = ({ onClose, onSuccess, facilityId }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (submitting) return;

    // A valid email looks like "name@domain.com": some text, an "@", more
    // text, a dot, then a couple more letters.
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email address");
      return;
    }

    try {
      setSubmitting(true);
      await userService.inviteUser({
        user_email: email.trim(),
        facility_id: facilityId ? Number(facilityId) : null,
      });
      toast.success("Invite sent successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Keep the modal open on any error so the user can fix and resubmit.
      if (detail === "Email already exists") {
        setError("Email already registered");
      } else {
        toast.error(detail || "Something went wrong! Please try again");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center transition-opacity">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Invite User</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              We'll email an invite link so they can set up their own account.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="Enter email address"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:border-blue-500 transition"
              />
              {error && <p className="text-xs text-red-500 mt-1.5 ml-1">{error}</p>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-2.5 text-sm font-medium rounded-full border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2.5 text-sm font-medium rounded-full bg-[#2B3674] text-white hover:bg-[#232d61] transition disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {submitting ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default InviteUserModalV2;
